import React, { useState } from 'react';

import { IconButton, LinearProgress, Menu, MenuItem, Typography } from '@mui/material';
import { AlineButton, CreateWorkflowDialog } from 'src/components';
import { MoreVerticalIcon } from 'src/components/icons/MoreVerticalIcon';
import {
  CrmVaultAction,
  ReconnectIntegrationButton,
} from 'src/components/send-for-signature/components/AddIntegration';
import { LinkOpportunity } from 'src/editor/toolbars/right-panel/integrations/LinkOpportunity';
import type { Apollo } from 'src/gql/generated';
import { INTEGRATION_DISPLAY_NAME } from 'src/helpers/integration-display-name';
import {
  getIsIntegrationCallable,
  useGetCurrentDoc,
  useGetVariablesForDocument,
  useUserIntegrations,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  header: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'nowrap',
    height: 48,
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
  },
  wrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

export function CRMIntegrationDrawer({ integration }: { integration: Apollo.Integration }) {
  const { classes } = useStyles();
  const { document } = useGetCurrentDoc();
  const { allConnections, initialLoading } = useUserIntegrations();
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!document) return <LinearProgress />;

  const isConnectionCallable = getIsIntegrationCallable(allConnections, integration);
  const actionLabel = isConnectionCallable ? 'Manage' : 'Connect';

  return (
    <CrmVaultAction integration={integration}>
      <div className={classes.wrapper}>
        <div className={classes.header}>
          <Typography variant="subtitle1">{INTEGRATION_DISPLAY_NAME[integration]}</Typography>
          <IconButton ref={setAnchorEl} size="small" onClick={() => setOpen(!open)}>
            <MoreVerticalIcon />
          </IconButton>
        </div>
        <CRMIntegrationBody
          documentId={document.id}
          documentVariant={document.variant}
          integration={integration}
          workflowId={document.workflow?.id ?? null}
        />
      </div>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setOpen(false)}
      >
        <CrmVaultAction.Control placement="left">
          {({ disabled, open: openVault }) => (
            <MenuItem
              // Until the list loads, a connected Salesforce would read as new and get the first-connect dialog.
              disabled={disabled || initialLoading}
              onClick={() => {
                setOpen(false);
                openVault();
              }}
            >{`${actionLabel} ${INTEGRATION_DISPLAY_NAME[integration]}`}</MenuItem>
          )}
        </CrmVaultAction.Control>
      </Menu>
    </CrmVaultAction>
  );
}

function CRMIntegrationBody({
  documentId,
  documentVariant,
  integration,
  workflowId,
}: {
  documentId: UUID;
  documentVariant: Apollo.DocumentVariant;
  integration: Apollo.Integration;
  workflowId: UUID | null;
}) {
  const { allConnections, initialLoading } = useUserIntegrations();
  const isCallable = getIsIntegrationCallable(allConnections, integration);

  if (initialLoading) return <LinearProgress />;
  if (!isCallable)
    return <DisconnectedCrmPanel documentId={documentId} integration={integration} />;
  if (!workflowId) return <CRMNoWorkflow integration={integration} />;

  return (
    <LinkOpportunity
      documentId={documentId}
      documentVariant={documentVariant}
      integration={integration}
      workflowId={workflowId}
    />
  );
}

const useNoWorkflowStyles = makeStyles()(theme => ({
  container: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    height: '100%',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  fieldList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    maxWidth: 280,
    width: '100%',
  },
}));

interface CRMNoWorkflowProps {
  integration: Apollo.Integration;
}

function CRMNoWorkflow({ integration }: CRMNoWorkflowProps) {
  const { classes } = useNoWorkflowStyles();
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <div className={classes.container}>
        <Typography
          align="center"
          variant="subtitle1"
        >{`Create a workflow to use ${INTEGRATION_DISPLAY_NAME[integration]}`}</Typography>
        <AlineButton color="primary" variant="contained" onClick={() => setOpen(!open)}>
          Create
        </AlineButton>
      </div>
      <CreateWorkflowDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function DisconnectedCrmPanel({
  documentId,
  integration,
}: {
  documentId: UUID;
  integration: Apollo.Integration;
}) {
  const { classes } = useNoWorkflowStyles();
  const { variables } = useGetVariablesForDocument(documentId);
  const crmName = INTEGRATION_DISPLAY_NAME[integration];
  const fieldLabels = [
    ...new Set(
      variables.flatMap(variable =>
        variable.integrationLink?.integration === integration && variable.label
          ? [variable.label]
          : []
      )
    ),
  ];

  return (
    <div className={classes.container}>
      <Typography align="center" variant="subtitle1">
        {`${crmName} is disconnected`}
      </Typography>
      <Typography align="center" color="textSecondary" variant="body2">
        Connect {crmName} to add and sync fields.
      </Typography>
      <ReconnectIntegrationButton integration={integration} />
      {fieldLabels.length > 0 && (
        <div className={classes.fieldList}>
          {fieldLabels.map(label => (
            <Typography key={label} align="center" color="textSecondary" variant="body2">
              {label}
            </Typography>
          ))}
        </div>
      )}
    </div>
  );
}
